import type { DeepReadonly, InjectionKey, MaybeRefOrGetter, WatchStopHandle } from 'vue';
import type { EventHookCallback, EventHookRegister } from '#layers/base';
import type { OnBlockTreeSlotClickPayload } from '#layers/block-engine/composables/block-editor/tree';
import type {
	BlockDefinition,
	BlockSerialized,
	BlockSerializedChild,
} from '#layers/block-engine/types/block';
import type { BlockRootContextApi, BlockRootContextController } from './root-context';
import { inject } from 'vue';
import { noop } from '#layers/base';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { useBlockInEditor } from '#layers/block-engine/utils/block/api/helpers';
import { blockPreviewIdPrefix } from '#layers/block-engine/utils/block/const';
import { isBlockSerialized } from '#layers/block-engine/utils/block/editor';

export interface BlockEditorContextEventPayload {
	childId: string;
}

interface InsertBlockOptions {
	target?: {
		id?: string;
		index?: number;
		slot?: string;
	};
	block: string;
	props?: Record<string, any>;
}

interface MoveBlockOptions {
	/**
	 * The child ID of the block to move.
	 */
	childId: string;
	target?: {
		/**
		 * The target child ID to move the block into. Defaults to the current block instance (the one calling the method).
		 */
		id?: string;
		/**
		 * The index within the slot to insert the block. Defaults to the end of the slot.
		 */
		index?: number;
		/**
		 * The target slot name. Defaults to 'default'.
		 */
		slot?: string;
	};
}

export interface useBlockInEditorContext<BlockProps extends Record<string, any> = Record<string, any>> {
	blockInEditor: boolean;
	slotIsEmpty: (slot?: string) => boolean;
	getSlot: (name: string) => BlockSerializedChild[];
	onBlockTreeSlotClick: EventHookRegister<Omit<OnBlockTreeSlotClickPayload, 'childId'>>;
	getProps: () => BlockProps;
	setProp: <K extends keyof BlockProps>(prop: K, value: BlockProps[K]) => void;
	insertBlock: (options: InsertBlockOptions) => Promise<BlockSerializedChild>;
	/**
	 * Move a block into a slot of this block instance.
	 */
	moveBlock: (options: MoveBlockOptions) => void;
	isPreview: boolean;
	triggerLayoutUpdate: () => void;
}

/**
 * API to interact with the block and the block editor.
 */
export function useBlockInEditorContext<BlockProps extends Record<string, any> = Record<string, any>>(): useBlockInEditorContext<BlockProps> {
	const blockInEditor = useBlockInEditor();

	if (!blockInEditor) {
		return {
			blockInEditor,
			slotIsEmpty: () => false,
			getSlot: () => [],
			onBlockTreeSlotClick: () => {
				return {
					off: noop,
				};
			},
			getProps: () => ({} as BlockProps),
			setProp: noop,
			insertBlock: noop as any,
			moveBlock: noop,
			isPreview: false,
			triggerLayoutUpdate: noop,
		};
	}

	const ctx = useBlockEditorContext();

	/**
	 * Use `getChild` in functions to retain reactivity.
	 *
	 * Don't do `const child = getChild()` at the top level of `useBlockInEditorContext`, as it will not update when the child changes.
	 */
	const getChild = useBlockChild();
	// It's okay to store the child in this scope as it will not change
	const childId = getChild().id;

	function slotIsEmpty(slot = 'default') {
		return (getChild().slots[slot]?.length ?? 0) === 0;
	}

	function getProps(): BlockProps {
		return (getChild().props ?? {}) as BlockProps;
	}

	const setProp = ctx.updateChildProp.bind(null, childId) as <K extends keyof BlockProps>(prop: K, value: BlockProps[K]) => void;

	const onBlockTreeSlotClick = filteredEventHook(ctx.tree.slotClickEvent.on, childId);

	return {
		blockInEditor,

		// Slots
		slotIsEmpty,
		getSlot: (name) => {
			const child = getChild();

			if (!child.slots[name]) {
				return [];
			}

			return child.slots[name]?.map((c) => ctx.block.value?.children?.[c]).filter(Boolean) as BlockSerializedChild[] ?? [];
		},
		onBlockTreeSlotClick,

		// Props
		getProps,
		setProp,

		// Insertion
		insertBlock: async (options: InsertBlockOptions) => {
			const child = getChild();
			const childId = options.target?.id == null ? child.id : options.target.id;

			const index = options.target?.index ?? child.slots[options.target?.slot ?? 'default']?.length ?? 0;

			const newId = await ctx.insertion.add({
				props: options.props,
				blockId: options.block,
				target: {
					id: '',
					index,
					childId,
					slotName: options.target?.slot,
				},
				autoSelect: false,
			});

			if (!newId) {
				throw new Error('Failed to insert block');
			}

			const newChild = ctx.block.value?.children?.[newId];

			if (!newChild) {
				throw new Error(`Block with ID ${newId} not found in the block data`);
			}

			return newChild as BlockSerializedChild;
		},

		// Move
		moveBlock: (options: MoveBlockOptions) => {
			const child = getChild();
			const targetChildId = options.target?.id == null ? child.id : options.target.id;
			const slot = options.target?.slot ?? 'default';
			const index = options.target?.index ?? child.slots[slot]?.length ?? 0;

			ctx.moveBlock({
				childId: options.childId,
				to: {
					parentChildId: targetChildId,
					slot,
					index,
				},
			});
		},

		isPreview: childId.startsWith(blockPreviewIdPrefix),

		triggerLayoutUpdate: () => {
			ctx.bounds.updateEvent.trigger(null);
		},
	};
}

function filteredEventHook<P extends BlockEditorContextEventPayload>(eventHook: EventHookRegister<P>, childId: string) {
	return (callback: EventHookCallback<Omit<P, 'childId'>>) => {
		return eventHook((payload) => {
			if (payload.childId === childId) {
				return callback(payload as Omit<P, 'childId'>);
			}
		});
	};
}

interface BlockContextBase {
	getBlockDefinition: () => BlockDefinition | BlockSerialized;
	getRootBlock: () => BlockDefinition | DeepReadonly<BlockSerialized>;
	/**
	 * Triggers an event on the root block.
	 */
	emitOnRootBlock: (event: string, payload?: any) => void;
}

interface BlockContextInjection extends BlockContextBase, BlockRootContextController {}

export interface BlockContext extends BlockContextBase, BlockRootContextApi {
	getChild: () => BlockSerializedChild;
	isRootChild: () => boolean;
}

export const blockContextKey = Symbol('blockContextKey') as InjectionKey<BlockContextInjection>;

export const blockChildKey = Symbol('blockChildKey') as InjectionKey<() => BlockSerializedChild>;

/**
 * An API to interact with the block tree and renderer. Works outside of the block editor.
 */
export function useBlockContext(): BlockContext {
	const blockContext = inject(blockContextKey, null);

	if (!blockContext) {
		throw new Error('useBlockContext must be used within a Block component');
	}

	const getChild = useBlockChild();

	return {
		getBlockDefinition: blockContext.getBlockDefinition,
		getRootBlock: blockContext.getRootBlock,
		getRootContext: blockContext.getRootContext,
		getRootContextProperty: blockContext.getRootContextProperty,
		emitOnRootBlock: blockContext.emitOnRootBlock,
		getChild,
		isRootChild: () => {
			const child = getChild();
			const rootBlock = blockContext.getRootBlock();
			return !!(isBlockSerialized(rootBlock) && rootBlock.rootChildren?.includes(child.id));
		},
	};
}

export function useBlockChild(): () => BlockSerializedChild {
	const getChild = inject(blockChildKey, null);

	if (!getChild) {
		throw new Error('useBlockChild must be used within a Block component');
	}

	return getChild;
}

/**
 * Publishes a reactive value to the current block render's root context.
 */
export function exposeToBlockRootContext<TValue = unknown>(
	key: string,
	value: MaybeRefOrGetter<TValue | undefined>,
): WatchStopHandle | undefined {
	const blockContext = inject(blockContextKey, null);

	if (!blockContext) {
		return;
	}

	const getChild = inject(blockChildKey, null);
	const initialChild = getChild?.() ?? null;

	return blockContext.publishRootContextProperty(key, value, {
		blockKey: initialChild ?? Symbol(key),
		getBlock: () => ({
			child: getChild?.() ?? initialChild,
			definition: blockContext.getBlockDefinition(),
			rootBlock: blockContext.getRootBlock(),
		}),
	});
}
